import { useEffect, useRef, useState } from "react";
import { Button, Flex, Text } from "@radix-ui/themes";
import { FiCheck, FiCopy } from "react-icons/fi";
import { IoCheckmarkCircle, IoCloseCircle } from "react-icons/io5";
import { creatorsControllerDetailCreator } from "@/lib/lutely-api/gen/creators/creators";
import { SITE_URL } from "@/lib/config/env-var.config";
import { cn } from "@/lib/utils";

const MAX_SLUG = 100;
const APPROACHING_THRESHOLD = 10;

type SlugStatus = "idle" | "checking" | "available" | "taken" | "error";

export type InlineNameEditSectionProps = {
  initialName: string;
  initialSlug: string | null | undefined;
  avatarUrl: string | null | undefined;
  onAvatarClick: () => void;
  isSaving: boolean;
  onSave: (name: string, slug: string | null) => Promise<void>;
  onDirtyChange?: (dirty: boolean) => void;
  /** Rendered under the avatar — the dashboard puts the banner control here. */
  avatarAccessory?: React.ReactNode;
  /** Rings the still-empty required fields, for the Complete profile entry. */
  highlightRequired?: boolean;
  /**
   * Reports the live draft so a parent can save every section at once.
   * `slugBlocked` mirrors the check this section's own Save enforces.
   */
  onValuesChange?: (values: {
    name: string;
    slug: string | null;
    slugBlocked: boolean;
  }) => void;
};

export function InlineNameEditSection({
  initialName,
  initialSlug,
  avatarUrl,
  onAvatarClick,
  isSaving,
  onSave,
  onDirtyChange,
  avatarAccessory,
  highlightRequired = false,
  onValuesChange,
}: InlineNameEditSectionProps) {
  const [name, setName] = useState(initialName);
  const [slug, setSlug] = useState(initialSlug ?? "");
  const [slugStatus, setSlugStatus] = useState<SlugStatus>("idle");
  const [copied, setCopied] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const checkIdRef = useRef(0);

  const isDirty = name !== initialName || slug !== (initialSlug ?? "");
  const charsLeft = MAX_SLUG - slug.length;
  const isAtMax = charsLeft === 0;
  const isApproaching = charsLeft <= APPROACHING_THRESHOLD && charsLeft > 0;

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    onValuesChange?.({
      name,
      slug: slug.trim() || null,
      slugBlocked: slugStatus === "taken" || slugStatus === "error",
    });
  }, [name, slug, slugStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const trimmed = slug.trim();
    if (!trimmed || trimmed === (initialSlug ?? "")) {
      setSlugStatus("idle");
      return;
    }
    setSlugStatus("checking");
    clearTimeout(debounceRef.current);
    const checkId = ++checkIdRef.current;
    debounceRef.current = setTimeout(async () => {
      try {
        await creatorsControllerDetailCreator(trimmed);
        if (checkIdRef.current === checkId) setSlugStatus("taken");
      } catch (err: unknown) {
        if (checkIdRef.current !== checkId) return;
        const status = (err as { response?: { status?: number } })?.response
          ?.status;
        if (status === 404) {
          setSlugStatus("available");
        } else {
          setSlugStatus("error");
        }
      }
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [slug, initialSlug]);

  function getBorderClass() {
    if (highlightRequired && !slug.trim())
      return "border-amber-500 ring-2 ring-amber-200";
    if (isAtMax) return "border-red-500";
    if (isApproaching) return "border-amber-500";
    if (slugStatus === "available") return "border-green-500";
    if (slugStatus === "taken") return "border-red-500";
    if (slugStatus === "error") return "border-amber-500";
    return "border-gray-300 focus-within:border-blue-500";
  }

  function getHelperContent() {
    if (isAtMax)
      return (
        <Text size="1" className="text-red-500">
          Maximum length reached
        </Text>
      );
    if (isApproaching)
      return (
        <Text size="1" className="text-amber-500">
          Almost at the {MAX_SLUG} character limit
        </Text>
      );
    if (slugStatus === "available")
      return (
        <Flex align="center" gap="1">
          <IoCheckmarkCircle className="text-green-500 w-3.5 h-3.5" />
          <Text size="1" className="text-green-500">
            Available
          </Text>
        </Flex>
      );
    if (slugStatus === "taken")
      return (
        <Flex align="center" gap="1">
          <IoCloseCircle className="text-red-500 w-3.5 h-3.5" />
          <Text size="1" className="text-red-500">
            That handle is already taken
          </Text>
        </Flex>
      );
    if (slugStatus === "error")
      return (
        <Text size="1" className="text-amber-600">
          Unable to verify availability
        </Text>
      );
    return (
      <Text size="1" className="text-[#525966]">
        Letters, numbers, dashes, and underscores only. Cannot start or end with
        a hyphen. {MAX_SLUG} characters max.
      </Text>
    );
  }

  function handleCopyUrl() {
    const urlToCopy = initialSlug ? `${SITE_URL}/@${initialSlug}` : SITE_URL;
    navigator.clipboard.writeText(urlToCopy).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  async function handleSave() {
    // The parent toasts and rethrows on failure; swallow here so the
    // rejection doesn't surface as an unhandled promise. Dirtiness is
    // props-derived, so a failed save keeps the section dirty by itself.
    try {
      await onSave(name, slug.trim() || null);
    } catch {
      /* already surfaced by the parent */
    }
  }

  function handleCancel() {
    setName(initialName);
    setSlug(initialSlug ?? "");
  }

  const saveDisabled =
    !isDirty ||
    isSaving ||
    slugStatus === "taken" ||
    slugStatus === "error" ||
    slugStatus === "checking";

  return (
    <Flex direction="column" gap="5">
      <Flex direction={{ initial: "column", sm: "row" }} gap="6" align="start">
        {/* Avatar, plus whatever the caller stacks beneath it */}
        <Flex
          direction="column"
          gap="3"
          className="shrink-0 mx-auto sm:mx-0 items-center"
        >
          <div className="relative group w-[200px] h-[200px] shrink-0">
            <div
              className={cn(
                "w-full h-full rounded-full overflow-hidden border-2 border-gray-200",
                highlightRequired &&
                  !avatarUrl &&
                  "border-amber-500 ring-2 ring-amber-200",
              )}
            >
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <Flex
                  align="center"
                  justify="center"
                  className="w-full h-full bg-gray-200"
                >
                  <svg
                    className="w-8 h-8 text-gray-400"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                  </svg>
                </Flex>
              )}
            </div>
            <button
              type="button"
              onClick={onAvatarClick}
              className="absolute inset-0 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity cursor-pointer bg-black/30"
              aria-label="Update profile image"
            >
              <span
                className="text-blue-900 text-[14px] font-semibold"
                style={{
                  display: "flex",
                  height: "36px",
                  padding: "0 20px",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "100px",
                  background: "#FFF",
                }}
              >
                Update Image
              </span>
            </button>
          </div>
          {avatarAccessory}
        </Flex>

        {/* Fields */}
        <Flex direction="column" gap="5" style={{ flex: 1 }}>
          <Flex direction="column" gap="1">
            <Text
              as="label"
              htmlFor="creator-display-name"
              size="2"
              weight="medium"
              className="text-blue-900"
            >
              Display Name
            </Text>
            <input
              id="creator-display-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name fans will see"
              className={cn(
                "w-full px-3 py-2 text-sm border border-gray-300 rounded-md outline-none focus:border-blue-500 transition-colors",
                highlightRequired &&
                  !name.trim() &&
                  "border-amber-500 ring-2 ring-amber-200",
              )}
            />
            {highlightRequired && !name.trim() && (
              <Text size="1" className="text-amber-600">
                Required to complete your profile
              </Text>
            )}
          </Flex>

          <Flex direction="column" gap="1">
            <Text
              as="label"
              htmlFor="creator-slug"
              size="2"
              weight="medium"
              className="text-blue-900"
            >
              Your Lutely URL
            </Text>
            <Flex align="center" gap="2">
              <Flex
                align="stretch"
                className={cn(
                  "flex-1 border rounded-md overflow-hidden transition-colors bg-white",
                  getBorderClass(),
                )}
              >
                <span className="flex items-center px-3 bg-blue-100 text-blue-900 text-sm shrink-0 whitespace-nowrap">
                  {SITE_URL}/@
                </span>
                <input
                  id="creator-slug"
                  value={slug}
                  onChange={(e) =>
                    setSlug(e.target.value.replace(/[^a-zA-Z0-9_-]/g, ""))
                  }
                  placeholder="your_name"
                  maxLength={MAX_SLUG}
                  className="flex-1 px-3 py-2 text-sm outline-none bg-transparent min-w-0"
                />
                {(isAtMax || isApproaching) && (
                  <span
                    className={cn(
                      "flex items-center pr-3 text-sm font-medium shrink-0",
                      isAtMax ? "text-red-500" : "text-amber-500",
                    )}
                  >
                    {charsLeft} left
                  </span>
                )}
                {!isAtMax && !isApproaching && slugStatus === "available" && (
                  <span className="flex items-center pr-3 text-green-500">
                    <IoCheckmarkCircle className="w-4 h-4" />
                  </span>
                )}
                {!isAtMax && !isApproaching && slugStatus === "taken" && (
                  <span className="flex items-center pr-3 text-red-500">
                    <IoCloseCircle className="w-4 h-4" />
                  </span>
                )}
              </Flex>
              <button
                type="button"
                onClick={handleCopyUrl}
                title={
                  initialSlug
                    ? `Copy ${SITE_URL}/@${initialSlug}`
                    : "Copy profile URL"
                }
                className="flex items-center justify-center w-8 h-8 rounded-md text-[#525966] hover:text-[#050C2B] hover:bg-gray-100 transition-colors flex-shrink-0"
              >
                {copied ? (
                  <FiCheck className="w-4 h-4 text-green-500" />
                ) : (
                  <FiCopy className="w-4 h-4" />
                )}
              </button>
            </Flex>
            {getHelperContent()}
          </Flex>
        </Flex>
      </Flex>

      <Flex align="center" justify="end" gap="3">
        <button
          type="button"
          onClick={handleCancel}
          disabled={!isDirty}
          className="text-sm text-[#525966] hover:text-[#050C2B] transition-colors disabled:opacity-40 disabled:pointer-events-none"
        >
          Cancel
        </button>
        <Button
          size="2"
          radius="full"
          onClick={handleSave}
          disabled={saveDisabled}
        >
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </Flex>
    </Flex>
  );
}
